import type { Dayjs } from "dayjs";
import type { Dates } from "src/types/shared/cookies";
import { getDaysInMonth, isSameMonth } from "src/website-apps/shared/utils/calendar";

/**
 * Whether monthDate falls in the same calendar month as today. Compares native getters since wrapping
 * monthDate in dayjs would read the machine's local timezone. Used for comparing venue-local dates
 * against the viewer's local today (e.g., trimming past days from the current-month grid).
 */
export const isSameMonthAs = (today: Dayjs, monthDate: Date): boolean =>
  monthDate.getFullYear() === today.year() && monthDate.getMonth() === today.month();

/** Whether monthDate's calendar month is strictly before today's; see isSameMonthAs for when/why. */
export const isMonthInPast = (today: Dayjs, monthDate: Date): boolean =>
  monthDate.getFullYear() < today.year() ||
  (monthDate.getFullYear() === today.year() && monthDate.getMonth() < today.month());

/**
 * Creates an array of padding days with stable keys.
 * Padding days are days from the previous/next month
 * that are displayed in the calendar. The padding days are used to fill the empty spaces.
 * @param count - The number of padding days to create.
 * @param prefix - Prefix for stable key generation
 * @param suffix - Suffix type for the padding (start or end)
 * @returns Array of padding days.
 */
const createPaddingDays = (count: number, prefix: string, suffix: "start" | "end") =>
  Array.from({ length: count }, (_, index) => ({
    day: null,
    key: `${prefix}-pad-${suffix}-${index}`,
  }));

/**
 * Gets the days that should be rendered for a given month based on date filter and current date logic
 * @param monthDate - The month to get days for
 * @param dateFilter - Optional date filter to constrain days
 * @param today - Request-scoped "today" from SSR-safe useStableNow().
 * @returns Array of day numbers that should be rendered
 */
export const getDaysToRender = (monthDate: Date, dateFilter: Maybe<Dates>, today: Dayjs): number[] => {
  const daysInMonth = getDaysInMonth(monthDate);

  if (dateFilter?.dates.from && dateFilter.dates.to) {
    // Scenario 2: Date selector is set - show days within date filter range
    const fromDate = new Date(dateFilter.dates.from);
    const toDate = new Date(dateFilter.dates.to);

    // Get the start and end day for this specific month within the filter range
    const startDay = isSameMonth(monthDate, fromDate) ? fromDate.getDate() : 1;
    const endDay = isSameMonth(monthDate, toDate) ? toDate.getDate() : daysInMonth;

    return Array.from({ length: endDay - startDay + 1 }, (_, i) => startDay + i);
  }
  // Scenario 1: No date selector - current month shows today onwards, others show all days
  const isCurrentMonth = isSameMonthAs(today, monthDate);
  const startDay = isCurrentMonth ? today.date() : 1;
  return Array.from({ length: daysInMonth - startDay + 1 }, (_, i) => startDay + i);
};

/**
 * Generates an array of calendar days for the given month + date filter.
 * @param monthDate - The month to generate days for
 * @param dateFilter - Optional date filter
 * @param today - Request-scoped "today" from SSR-safe useStableNow().
 * @returns Array of calendar days with padding for grid layout
 */
export const getCalendarDays = (monthDate: Date, dateFilter: Maybe<Dates>, today: Dayjs) => {
  const daysToRender = getDaysToRender(monthDate, dateFilter, today);
  const [firstDayToRender] = daysToRender;

  if (!firstDayToRender) return [];

  // Create stable month prefix for keys
  const monthKey = `${monthDate.getFullYear()}-${monthDate.getMonth() + 1}`;

  // Calculate padding needed to align the first day we're showing with the correct weekday
  const firstDayWeShow = new Date(monthDate.getFullYear(), monthDate.getMonth(), firstDayToRender);
  const paddingDays = createPaddingDays(firstDayWeShow.getDay(), monthKey, "start");

  // Create month days with stable keys based on the day number
  const monthDays = daysToRender.map((day) => ({
    day,
    key: `${monthKey}-day-${day}`,
  }));

  // Add end padding only if we show the complete month to the last day
  const showsToLastDay = daysToRender.at(-1) === getDaysInMonth(monthDate);
  let endPaddingDays: { day: null; key: string }[] = [];

  if (showsToLastDay) {
    const lastDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), getDaysInMonth(monthDate));
    const endPaddingCount = 6 - lastDayOfMonth.getDay();
    endPaddingDays = endPaddingCount > 0 ? createPaddingDays(endPaddingCount, monthKey, "end") : [];
  }

  return [...paddingDays, ...monthDays, ...endPaddingDays];
};

/**
 * Formats a date as YYYY-MM-DD string for use as date identifiers
 * @param date - The date to format
 * @returns Date string in YYYY-MM-DD format
 */
export const formatDateString = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * Parses a YYYY-MM-DD string into a Date using local time (avoids UTC offset issues).
 */
export const parseDateString = (dateString: string): Date => {
  const [y, m, d] = dateString.split("-").map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
};
